import { MutationData, mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ReactNode, RefObject, useRef, useState } from 'react';
import useLast from 'use-last';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogBody,
  DialogContentHeadless,
  DialogContentLayout,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { AnimatedHeight } from 'v5/primitives/AnimatedHeight';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';
import { DialogErrorFooter } from './DialogErrorFooter';

/**
 * Two-step "create entity" dialog, entity-agnostic:
 *
 * - `setup`: the choices the form depends on (resource type + visibility,
 *   production type + workspace, …) with a "Next" button;
 * - `form`: the entity form, with a recap of the setup choices pinned under
 *   the title and "Back" plus the save actions in the footer.
 *
 * The entity form stays mounted across both steps (it hides the inactive
 * block) so typed values survive going back. Wire an entity with
 * {@link EntityCreateDialogShell} (unsaved-changes protection + dialog
 * chrome) around {@link EntityCreateDialogContent} (steps, recap, footer).
 */
export type EntityCreateStep = 'setup' | 'form';

/** The steps in flow order. */
export const ENTITY_CREATE_STEPS: readonly EntityCreateStep[] = [
  'setup',
  'form'
];

export type EntityCreateFormState = {
  isSubmitting: boolean;
  isValid: boolean;
  isDirty: boolean;
};

export type EntityCreateFormHandle<TCreated> = {
  /** Resolves with the created entity; rejects on validation or request failure. */
  submit(): Promise<TCreated>;
};

/** One footer action on the form step. Exactly one should be `primary`. */
export type EntityCreateAction<TId extends string> = {
  id: TId;
  label: ReactNode;
  icon?: ReactNode;
  primary?: boolean;
  /** After a successful save: close the dialog, or reset it for another entry. */
  after: 'close' | 'reset';
};

export type EntityCreateShellContext = {
  /** Report the form's dirty state (dismiss protection). */
  onDirtyChange(isDirty: boolean): void;
  /** Close without the unsaved-changes prompt (after a successful save). */
  close(): void;
};

export interface EntityCreateDialogShellProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  children(context: EntityCreateShellContext): ReactNode;
}

export function EntityCreateDialogShell({
  open,
  onOpenChange,
  children
}: EntityCreateDialogShellProps) {
  const [isDirty, setIsDirty] = useState(false);
  return (
    <UnsavedChangesProtection
      isDirty={isDirty}
      open={open}
      onOpenChange={onOpenChange}
      beforeUnloadMessage="You have unsaved changes!"
    >
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless>
            {children({
              onDirtyChange: setIsDirty,
              close: () => onOpenChange(false)
            })}
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}

export type EntityCreateFormRenderProps<TCreated> = {
  step: EntityCreateStep;
  /** Changes on reset ("save & add another"); use it as the form's `key`. */
  formKey: number;
  formRef: RefObject<EntityCreateFormHandle<TCreated>>;
  onStateChange(state: EntityCreateFormState): void;
  /**
   * Wire to the form's submit shortcut (⌘/Ctrl+Enter): on the setup step it
   * goes to the form step, on the form step it runs the primary action —
   * never a bare form submit behind the dialog's back.
   */
  requestSubmit(): void;
};

export interface EntityCreateDialogContentProps<
  TCreated,
  TActionId extends string
> extends EntityCreateShellContext {
  title: Record<EntityCreateStep, ReactNode>;
  /** Screen-reader description of the dialog. */
  description: string;
  /** Recap of the setup choices, pinned under the title on the form step. */
  renderSummary?(props: { onEdit(): void; canEdit: boolean }): ReactNode;
  /**
   * Both steps. The body is not padded: a schema form spans it edge to edge
   * (`SCHEMA_FORM_GUTTER.lg`), anything else pads itself (`px-6 py-3`).
   */
  renderForm(props: EntityCreateFormRenderProps<TCreated>): ReactNode;
  /**
   * Form step only: a strip pinned between the scrolling form and the footer
   * buttons (e.g. a duplicate warning). `formKey` changes on reset.
   */
  renderFooterNotice?(props: { formKey: number }): ReactNode;
  /** Enables "Next" (e.g. once the setup data has loaded). */
  canProceed: boolean;
  actions: readonly EntityCreateAction<TActionId>[];
  onCreated(entity: TCreated, actionId: TActionId): void;
  /** Footer error text when the failure has no user-facing message. */
  errorFallback: string;
}

export function EntityCreateDialogContent<TCreated, TActionId extends string>({
  title,
  description,
  renderSummary,
  renderForm,
  renderFooterNotice,
  canProceed,
  actions,
  onCreated,
  errorFallback,
  onDirtyChange,
  close
}: EntityCreateDialogContentProps<TCreated, TActionId>) {
  const formRef = useRef<EntityCreateFormHandle<TCreated>>(null);
  const [formKey, setFormKey] = useState(0);
  const [step, setStep] = useState<EntityCreateStep>('setup');
  const [formState, setFormState] = useState<EntityCreateFormState>({
    isSubmitting: false,
    isValid: true,
    isDirty: false
  });

  const submit = promiseState.useMutation(
    async (action: EntityCreateAction<TActionId>) => {
      if (!formRef.current) throw new Error('The form is not ready yet');
      const entity = await formRef.current.submit();
      // Saved: nothing is unsaved any more, so closing must not prompt.
      onDirtyChange(false);
      if (action.after === 'reset') {
        setFormKey(key => key + 1);
        setStep('setup');
      }
      onCreated(entity, action.id);
      if (action.after === 'close') close();
    }
  );
  const mutation: MutationData<unknown, unknown> = submit.state;
  // A retry keeps the last error on screen instead of hiding and re-showing it.
  const shownResult = useLast(mutation, !mt.isInProgress(mutation)) ?? mutation;
  // The failure is shown above the footer; `track` rethrows it.
  const save = (action: EntityCreateAction<TActionId>) =>
    submit.track(action).catch(() => {});
  const primaryAction =
    actions.find(action => action.primary) ?? actions[actions.length - 1];

  const requestSubmit = () => {
    if (step === 'setup') {
      if (canProceed) setStep('form');
      return;
    }
    if (primaryAction && !formState.isSubmitting) {
      save(primaryAction);
    }
  };

  return (
    // `max-h-none`: the viewport limit moves to the animated content, the surface only wraps it
    <DialogContentLayout className="max-h-none max-w-180">
      {/* the height eases between the steps; the size limits sit on the content, so it never squeezes */}
      <AnimatedHeight contentClassName="flex max-h-[calc(100vh-var(--spacing)*16)] min-h-[min(450px,calc(100vh-4rem))] flex-col">
        <DialogDescription className="sr-only">{description}</DialogDescription>
        <DialogHeader>
          <DialogTitle>{title[step]}</DialogTitle>
        </DialogHeader>
        {step === 'form' && renderSummary && (
          <div className="border-b border-border-default px-6 py-3">
            {renderSummary({
              onEdit: () => setStep('setup'),
              // Held like Back while a save or an upload runs.
              canEdit: !formState.isSubmitting
            })}
          </div>
        )}
        {/* The form's sticky section tabs cast the top scroll shadow instead. */}
        <DialogBody renderTopShadow={false} className="p-0">
          {renderForm({
            step,
            formKey,
            formRef,
            onStateChange: state => {
              setFormState(state);
              onDirtyChange(state.isDirty);
            },
            requestSubmit
          })}
        </DialogBody>
        {step === 'form' && renderFooterNotice?.({ formKey })}
        {/* Its own row: beside Back and the save actions it gets no width. */}
        {step === 'form' && (
          <AnimatedHeight className="shrink-0">
            {mt.isInError(shownResult) && (
              <div className="px-1.5 pt-1.5 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none">
                <DialogErrorFooter
                  compact
                  mutation={shownResult}
                  renderError={error =>
                    getUserFacingErrorMessage(error) || errorFallback
                  }
                />
              </div>
            )}
          </AnimatedHeight>
        )}
        <DialogFooter className="mt-auto gap-2">
          {step === 'setup' ? (
            <Button
              variant="primary"
              onClick={() => setStep('form')}
              disabled={!canProceed}
            >
              Next
              <ArrowRight />
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => setStep('setup')}
                disabled={formState.isSubmitting}
              >
                <ArrowLeft />
                Back
              </Button>
              {actions.map(action =>
                action.primary ? (
                  <AsyncButton
                    key={action.id}
                    variant="primary"
                    mutation={mutation}
                    onClick={() => save(action)}
                    disabled={formState.isSubmitting}
                    leftIcon={action.icon}
                  >
                    {action.label}
                  </AsyncButton>
                ) : (
                  <Button
                    key={action.id}
                    variant="secondary"
                    onClick={() => save(action)}
                    disabled={formState.isSubmitting}
                  >
                    {action.icon}
                    {action.label}
                  </Button>
                )
              )}
            </>
          )}
        </DialogFooter>
      </AnimatedHeight>
    </DialogContentLayout>
  );
}
